import React, { useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { resolveMediaUrl } from "@/lib/api";

/**
 * Large, generously-spaced autoplaying video spotlight shown ONLY at the
 * top of the New Arrivals & Offers page — never on the homepage. Fully
 * admin-controlled via the "New Arrivals Video" tab in the Admin Portal.
 * Autoplays muted (browser policy requires this) with a tap-to-unmute
 * control, since audio-with-sound autoplay is blocked by all browsers.
 * Renders nothing if the admin hasn't uploaded a video yet.
 */
export const ArrivalsVideoSpotlight = () => {
  const { items, loading } = useProducts("arrivals_video");
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const item = items?.[0];
  const src = item?.media_url ? resolveMediaUrl(item.media_url) : null;

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  if (loading || !src) return null;

  return (
    <section className="w-full py-10 sm:py-14" data-testid="arrivals-video-spotlight">
      <div className="max-w-md sm:max-w-lg mx-auto px-4">
        <div className="relative rounded-3xl overflow-hidden border border-primary/30 shadow-elegant bg-black">
          <video
            ref={videoRef}
            src={src}
            className="w-full h-auto max-h-[80vh] object-contain"
            autoPlay
            muted
            loop
            playsInline
            data-testid="arrivals-video-spotlight-video"
          />
          <button
            type="button"
            onClick={toggleMute}
            className="absolute bottom-4 right-4 h-11 w-11 rounded-full bg-background/85 backdrop-blur flex items-center justify-center border border-primary/30 hover:bg-background transition-colors"
            aria-label={muted ? "Unmute video" : "Mute video"}
            data-testid="arrivals-video-spotlight-mute-btn"
          >
            {muted ? <VolumeX className="h-5 w-5 text-foreground" /> : <Volume2 className="h-5 w-5 text-primary" />}
          </button>
        </div>
      </div>
    </section>
  );
};
